import { getLob } from "../../../../services/authenticationService";
import { updateConfigRequestBody } from "../../../../services/managePoliciesService";
import { META_DATA_BATCH_API } from "../../../../services/networkService";
import {
  getNewConfiguration,
  validateMetaDataResponse,
} from "../../../../services/uiConfigService";
import { openPopup } from "../../../../stateManagement/actions/popupActions";
import { validateWithOtp } from "../../../../stateManagement/actions/validateOtpPopupActions";
import { configurationAttributeType } from "../../../../types";
import { metaDataBatchPayload } from "../../../../types/types";
import { manageUpdateAccessObj } from "../../../manageUpdateAccess/manageUpdateAccessTypes";
import {
  ID_MAP_DEFAULT_INFO,
  ID_MAP_DEFAULT_STATE,
  ID_SUPERVISOR_TAB_ICON_PATH,
  ID_TAB_ICON_PATH,
  MUST_DO_ACTION_ORDER_ID,
} from "./mustDoActionUtils";
import { SingleMustDoDataPointState, SelectedMustDoActionTask, InputLengthValidations } from "./types";
import * as Sentry from "@sentry/react";

export const checkIfInputIsUnique = (
  datapoints: Array<SingleMustDoDataPointState>,
  dp_id: string,
  value: string
): boolean => {
  // Ensure label is trimmed and converted to lowercase for consistent comparison
  const trimmedLabel = value?.trim().toLowerCase();

  // Iterate over datapoints
  const isUnique = !datapoints.some(
    (dp: SingleMustDoDataPointState) =>
      dp.isEditable &&
      dp.id !== dp_id &&
      dp.value?.trim().toLowerCase() === trimmedLabel
  );
  
  return isUnique; // Return true if unique, else false
};

export function checkForDataFieldsValidation(
  tasksArray: SelectedMustDoActionTask[],
  configDataObject: Record<string, SingleMustDoDataPointState[]>,
  inputLengthValidations : InputLengthValidations
) {
  // Validation 1: Check if at least one task is selected
  const isAnyTaskSelected = tasksArray.some((task) => task.selected);

  if (!isAnyTaskSelected) {
    // console.error("Validation failed: No tasks are selected.");
    return false; // Validation fails if no tasks are selected
  }

  // Validation 2: Check label length for editable items in configDataObject , only for those list item which are selected
  for (const key in configDataObject) {
    //if the task in taskArray name ( compare with key ) seleced is false then continue to next iteration
    const task = tasksArray.find((task) => task.actionId === key);
    if(task && task.selected === false)
        continue;
    const items = configDataObject[key];

    for (const item of items) {
      if (item.isEditable) {
        const labelLength = item.value.length;

        if (item.id === "unit") {
          // Special case for item with id = 'unit'
          const minLength = inputLengthValidations.min.unit;
          const maxLength = inputLengthValidations.max.unit;
          if (labelLength < minLength || labelLength > maxLength) {
            // console.error(
            //   `Validation failed: Label "${item.value}" for id "unit" is invalid. It must be > 0 and <= 10 characters.`
            // );
            return false; // Validation fails for invalid label length
          }
        } else {
          const minLength = inputLengthValidations.min.title;
          const maxLength = inputLengthValidations.max.title;
          // General case for other editable items
          if (labelLength < minLength || labelLength > maxLength) {
            // console.error(
            //   `Validation failed: Label "${item.value}" is invalid. It must be > 5 and < 25 characters.`
            // );
            return false; // Validation fails for any invalid label length
          }
        }
      }
    }
    if (!checkIfInputFieldsAreUnique(items)) {
      return false;
    }
  }

  return true; // All validations passed
}
export const checkIfInputFieldsAreUnique = (
  datapoints: SingleMustDoDataPointState[]
): boolean => {
  const uniqueLabels = new Set<string>(); // Create a Set to store unique labels
  let countEditable = 0; // Counter for editable datapoints

  // Iterate over the datapoints
  datapoints.forEach((dp: SingleMustDoDataPointState) => {
    if (dp.isEditable) {
      // Check if the data point is editable
      countEditable++; // Increment the count of editable data points
      uniqueLabels.add(dp.value?.trim().toLowerCase() || ""); // Add trimmed and lowercase label to the Set
    }
  });
  // Check if the size of the set equals the count of editable datapoints
  return uniqueLabels.size === countEditable;
};

export function getDefaultStateValueById(id: string) {
  return ID_MAP_DEFAULT_STATE[id];
}

export function getLabelInfoById(id: string) {
  return ID_MAP_DEFAULT_INFO[id];
}

export function getOrderByID(id: string) {
  return MUST_DO_ACTION_ORDER_ID[id];
}
export function getColorById(id: string) {
  const currentState = getDefaultStateValueById(id);
  for (const item of currentState) {
    if (item.id === "color") {
      return item.value;
    }
  }

  return "#000000";
}
export function getMustDoDefaultStateValueOfAll() {
  return ID_MAP_DEFAULT_STATE;
}
export function populateSavedConfigWithDefaultData(
  savedConfig: Record<string, SingleMustDoDataPointState[]>
): Record<string, SingleMustDoDataPointState[]> {
  const defaultData: Record<string, SingleMustDoDataPointState[]> =
    getMustDoDefaultStateValueOfAll();
  const populatedConfig = { ...savedConfig };
  for (const key in defaultData) {
    if (!(key in populatedConfig)) {
      populatedConfig[key] = defaultData[key];
    }
  }

  return populatedConfig;
}
export function getSelectedMustDoActionArrayFromObject(
  selectedMustDoAction: Record<string, SingleMustDoDataPointState[]>
): Array<any> {
  /**
   * This function generates an array of selected "Must Do" action items based on the provided
   * `selectedMustDoAction` object. It ensures items from the saved configuration are listed first.
   */
  const defaultConfig: Record<string, SingleMustDoDataPointState[]> =
    getMustDoDefaultStateValueOfAll();

  const keysNotPresent: string[] = [];

  // Collect missing keys
  for (const key in defaultConfig) {
    if (!(key in selectedMustDoAction)) {
      keysNotPresent.push(key);
    }
  }
  // Prepare the final array starting with saved actions
  const resultArray = Object.keys(selectedMustDoAction).map(
    (actionId, index) => {
      const id = index.toString();
      const infoLabel = getLabelInfoById(actionId);
      const color = getColorById(actionId);

      return {
        id,
        actionId,
        infoLabel,
        color,
        selected: true,
      };
    }
  );

  // Append missing actions from the default configuration
  keysNotPresent.forEach((actionId, index) => {
    const id = (resultArray.length + index).toString();
    const infoLabel = getLabelInfoById(actionId);
    const color = getColorById(actionId);

    resultArray.push({
      id,
      actionId,
      infoLabel,
      color,
      selected: false,
    });
  });
  return resultArray;
}

export async function saveConfiguration(
  stateData: Record<string, Array<SingleMustDoDataPointState>>
) {
  const configVal = await convertStateToConfigStructure(stateData);
  const lob = getLob();
  const finalConfig: metaDataBatchPayload = {
    features: [
      {
        domainName: "clientconfig",
        domainType: "must_do_action_configuration",
        domainValues: configVal,
        lob,
      },{
        domainName: "clientconfig",
        domainType: "salesrep_must_do_action_configuration",
        domainValues: configVal,
        lob,
      }
    ],
  };

  validateWithOtp(
    async (verifiedUser?: manageUpdateAccessObj, verifyResponse?: any) => {
      try {
        if (verifyResponse.status >= 200 || verifyResponse.status < 300) {
          const validationObj = validateMetaDataResponse(
            verifyResponse?.data?.features
          );
          if (validationObj.success) {
            openPopup("Success", "Configuration Updated");
            // curConfigRef.current = { ...newConfigRef.current };
            // getCatalogueConfiguration();
          } else {
            openPopup("Error", validationObj.message);
          }
        } else {
          throw new Error("Something went wrong");
        }
      } catch (err) {
        if (err instanceof Error) openPopup("Error", err?.message);
      }
    },
    undefined,
    "admin",
    META_DATA_BATCH_API,
    finalConfig,
    "PUT"
  );
}

function getDefaultValueOfKey(mustDoAction: string, keyId: string) {
  return getDefaultStateValueById(mustDoAction)?.find(
    (item) => item.id == keyId
  )?.value;
}

export async function convertStateToConfigStructure(
  stateData: Record<string, Array<SingleMustDoDataPointState>>
) {
  // generate mustDoActionCard config data
  const mustDoActionCardData = await getMustDoActionCardData(stateData);

  const oultetTabFilterData = getOutletTabFilters(stateData);

  return [
    {
      name: "mustDoActionCard",
      type: "list",
      value: mustDoActionCardData,
      docUrl: "https://applicate.atlassian.net/l/cp/90s18da0",
      description: "list of must do actions",
      defaultValue: [],
      possibleVals: ["Map[]"],
    },
    {
      name: "outletTabFilters",
      type: "list",
      value: oultetTabFilterData,
      docUrl: "https://applicate.atlassian.net/l/cp/90s18da0",
      description: "list of outlet Tab Filters",
      defaultValue: [],
      possibleVals: ["Map[]"],
    },
  ];
}

const getMustDoActionCardData = async (
  stateData: Record<string, Array<SingleMustDoDataPointState>>
) => {
  const currency = await getCurrencyFromConfig();
  const configKeys = [
    "id",
    "unit",
    "color",
    "heading",
    "subHeading",
    "targetLabel",
    "avgValueLabel",
    "actionTag",
  ];
  const data = Object.keys(stateData).map((key) => {
    const stateKeyValuePair: Record<string, string> = {};
    stateKeyValuePair["id"] = key;
    const dataPoints = stateData[key];
    for (let dp of dataPoints) {
      stateKeyValuePair[dp.id] = dp.value;
    }

    const stateToConfig: Record<string, string> = {};

    for (let configKey of configKeys) {
      stateToConfig[configKey] = stateKeyValuePair[configKey];
      if (configKey == "unit" && key == "salesTarget") {
        if (currency != null) {
          stateToConfig[configKey] = currency;
        }
      } else if (configKey == "targetLabel")
        stateToConfig[configKey] = "Target";
      else if (
        configKey == "avgValueLabel" &&
        stateToConfig[configKey].trim() != ""
      )
        stateToConfig[configKey] =
          stateToConfig[configKey] + " " + stateKeyValuePair["last7"];
      else if (configKey == "actionTag")
        stateToConfig[configKey] = stateKeyValuePair["heading"]?.toUpperCase();
      else if(configKey=="heading")
        stateToConfig[configKey] = stateKeyValuePair["heading"]?.toUpperCase();
    }

    return stateToConfig;
  });

  return data;
};

const getOutletTabFilters = (
  stateData: Record<string, Array<SingleMustDoDataPointState>>
) => {
  const configKeys = [
    "id",
    "heading",
    "subHeading",
    "tabSvgPath",
    "supervisorHeading",
    "supervisorTabSvgPath",
  ];

  const data = Object.keys(stateData).map((key) => {
    const stateKeyValuePair: Record<string, string> = {};
    stateKeyValuePair["id"] = key;
    const dataPoints = stateData[key];
    for (let dp of dataPoints) {
      stateKeyValuePair[dp.id] = dp.value;
    }

    const stateToConfig: Record<string, string> = {};

    for (let configKey of configKeys) {
      stateToConfig[configKey] = stateKeyValuePair[configKey];

      if (configKey === "heading") {
        if (key == "salesTarget") stateToConfig[configKey] = "Total";
        else {
          let val = "";
          const headingArr = stateKeyValuePair["heading"].split(" ");
          if (headingArr.length > 1) {
            val = headingArr.slice(0, headingArr.length - 1).join(" ");
          } else {
            val = stateKeyValuePair["heading"];
          }
          stateToConfig["heading"] = val;
        }
      } else if (configKey === "subHeading") {
        if (key == "salesTarget") stateToConfig[configKey] = "Outlets";
        else {
          let val = "";
          const headingArr = stateKeyValuePair["heading"].split(" ");
          if (headingArr.length > 1) {
            val = headingArr[headingArr.length - 1];
          }

          stateToConfig["subHeading"] = val;
        }
      } else if (configKey === "tabSvgPath") {
        stateToConfig[configKey] = ID_TAB_ICON_PATH[key];
      } else if (configKey === "supervisorTabSvgPath") {
        stateToConfig[configKey] = ID_SUPERVISOR_TAB_ICON_PATH[key];
      }
    }

    return stateToConfig;
  });

  return data;
};

export async function getMustDoSavedConfig(): Promise<
  Record<string, Array<SingleMustDoDataPointState>>
> {
  /*
  //1 . get saved config
  // 2. convert to state
      2.1 values from config set in state
      2.2 default values in config to be same 
  // 3. return
  */
  try {
    const resp = await getNewConfiguration("must_do_action_configuration");
    if (resp == null || resp == undefined) return {};
    const values: configurationAttributeType[] = resp?.domainValues;
    const state = convertConfigToState(values);
    return state;
  } catch (error) {
    Sentry.captureException(error);
    return {};
  }
}

interface MustDoConfigValue {
  actionTag: string;
  avgValueLabel: string;
  color: string;
  heading: string;
  id: string;
  subHeading: string;
  targetLabel: string;
  unit: string;
  [key: string]: string; // Index signature
}

const convertConfigToState = (values: Array<any>) => {
  const mustDoActionCardConfig =
    values.find((item) => item["name"] == "mustDoActionCard")?.["value"] ?? [];
  let newState: Record<string, Array<SingleMustDoDataPointState>> = {};

  // Populate newState based on the backend order from mustDoActionCardConfig
  mustDoActionCardConfig.forEach((mustDoConfigValue: MustDoConfigValue) => {
    const mustDoId = mustDoConfigValue.id;
    const defaultState = ID_MAP_DEFAULT_STATE[mustDoId];

    if (defaultState) {
      // Deep copy to avoid mutation
      newState[mustDoId] = defaultState.map((datapoint) => ({ ...datapoint }));

      newState[mustDoId].forEach((datapoint) => {
        const id = datapoint.id;

        // Overwrite with new values if available
        if (mustDoConfigValue[id] !== undefined) {
          datapoint.value = mustDoConfigValue[id];
        }

        // Special handling for certain keys
        if (id === "avgValueLabel") {
          const last7DayVal =
            newState[mustDoId].find((item) => item.id === "last7")?.value ?? "";
          const idx =
            last7DayVal === "" ? -1 : datapoint.value.indexOf(last7DayVal);
          if (idx !== -1) {
            datapoint.value = datapoint.value.substring(0, idx - 1);
          }
        }

        if (
          id === "tagHeading" ||
          id === "tagSubHeading" ||
          id === "supervisorHeading" ||
          id === "last7"
        ) {
          datapoint.value = getDefaultValueOfKey(mustDoId, id) ?? "";
        }
      });
    }
  });
  return newState;
};

const getCurrencyFromConfig = async () => {
  const resp = await getNewConfiguration("customer_configuration");
  if (resp == null || resp == undefined) return null;
  const values: configurationAttributeType[] = resp?.domainValues;
  return values?.find((item) => item.name == "currency")?.["value"] ?? null;
};
